import { GoogleTagManager } from '@next/third-parties/google'
import * as Sentry from '@sentry/nextjs'
import { NextPage } from 'next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import Router, { useRouter } from 'next/router'
import React, { ReactElement, Suspense, useEffect } from 'react'
import { Slide, ToastContainer } from 'react-toastify'
import { Provider } from 'urql'

import { Loading } from '@/components/Loading'
import { GenericError } from '@/components/app-errors/GenericError'
import { MainAppLayout } from '@/components/layout/app'
import { useUrqlClient } from '@/urql'

import '@/flutter'

// import to start history tracking
import '@/NextHistory'

import { UpdateMonitor } from '@/update-monitor/UpdateMonitor'

import 'react-toastify/dist/ReactToastify.css'
import '../styles/globals.scss'
import '../styles/react-ranger.scss'

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => JSX.Element
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const SCROLL_POSITIONS = new Map<string, number>()

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const client = useUrqlClient()
  const getLayout =
    Component.getLayout ?? ((page) => <MainAppLayout>{page}</MainAppLayout>)

  const router = useRouter()

  useEffect(() => {
    const isWeb = !!Router.pathname.match(/^\/web\/?/)
    Sentry.setTag('logic_app', isWeb ? 'guardian_web' : 'guardian_app')
  }, [])

  useEffect(() => {
    function onRouteChangeComplete() {
      const key = window.history.state.key
      if (key && SCROLL_POSITIONS.get(key) != null)
        window._logiq_guardian_scroll_position = SCROLL_POSITIONS.get(key)
      else window._logiq_guardian_scroll_position = undefined
    }

    router.events.on('routeChangeComplete', onRouteChangeComplete)

    window._logiq_guardian_scroll_position_set = function (
      key: string,
      scroll: number
    ) {
      SCROLL_POSITIONS.set(key, scroll)
    }

    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete)
      delete window._logiq_guardian_scroll_position_set
    }
  }, [router])

  return (
    <UpdateMonitor>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Head>

      <Sentry.ErrorBoundary fallback={GenericError}>
        <Suspense
          fallback={
            <div className="pointer-events-none fixed inset-0 grid place-items-center">
              <Loading />
            </div>
          }
        >
          <Provider value={client}>
            <>
              {getLayout(<Component {...pageProps} />)}
              <MemoToastContainer
                hideProgressBar={true}
                transition={Slide}
                icon={false}
                closeButton={false}
              />
            </>
          </Provider>
        </Suspense>
      </Sentry.ErrorBoundary>
      {process.env.NEXT_PUBLIC_GTM_ID && (
        <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID} />
      )}
    </UpdateMonitor>
  )
}
export default MyApp

const MemoToastContainer = React.memo(ToastContainer)
